/**
 * Klassen zusammenfuehren — dieselbe Fassung wie in der ganzen Plattform
 * (packages/foundation/src/cn.ts kennt die Schriftstufen fuer tailwind-merge).
 */
export { cn } from "@ansichtssache/foundation";

/** Zahlen und Betraege auf Deutsch — die Fassung steht in `number-format.ts`. */
export {
  formatCurrency,
  formatAmount,
  formatDecimal,
  formatQuantity,
  formatHoursDecimal,
  formatPercent,
  parseDecimal,
  parseAmountCents,
  toCanonical,
} from "./number-format";

import { formatCurrency } from "./number-format";

/**
 * Euro → „72,00 €“. Ansichtstypen und Schema tragen Betraege in Euro
 * (Decimal), `formatCurrency` erwartet Cent — fuer Euro-Werte immer diese
 * Fassung nehmen.
 */
export function formatEuro(betrag: number | null | undefined): string {
  if (betrag === null || betrag === undefined) return "";
  return formatCurrency(Math.round(betrag * 100));
}

/** Dezimalstunden → "5h 30m" */
export function formatHours(hours: number): string {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/**
 * Zeitpunkte immer in deutscher Zeit, egal wo Server oder Browser stehen —
 * sonst rendern beide verschieden (Hydration) und um Mitternacht kippt der Tag.
 * Reine Kalenderdaten („2026-10-23“) haben keine Uhrzeit und werden als
 * solche formatiert (UTC), damit westlich von UTC nicht der Vortag erscheint.
 */
const ZEITZONE = "Europe/Berlin";
const NUR_DATUM = /^\d{4}-\d{2}-\d{2}$/;
const DATUM = new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: ZEITZONE });
const KALENDERDATUM = new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "UTC" });
const DATUM_ZEIT = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: ZEITZONE,
});

/** 23.10.2026 — leer bei fehlendem Datum. */
export function formatDate(value: Date | string | null | undefined): string {
  if (!value) return "";
  if (typeof value === "string" && NUR_DATUM.test(value)) {
    const d = new Date(`${value}T00:00:00Z`);
    return Number.isNaN(d.getTime()) ? "" : KALENDERDATUM.format(d);
  }
  const d = typeof value === "string" ? new Date(value) : value;
  return Number.isNaN(d.getTime()) ? "" : DATUM.format(d);
}

/** 23.10.2026, 19:30 */
export function formatDateTime(value: Date | string | null | undefined): string {
  if (!value) return "";
  const d = typeof value === "string" ? new Date(value) : value;
  return Number.isNaN(d.getTime()) ? "" : DATUM_ZEIT.format(d);
}

/** Alter in vollen Jahren zum Stichtag (Standard: heute). */
export function ageAt(birthDate: Date | null | undefined, at: Date = new Date()): number | null {
  if (!birthDate) return null;
  let age = at.getFullYear() - birthDate.getFullYear();
  const m = at.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && at.getDate() < birthDate.getDate())) age--;
  return age;
}

/** Initialen fuer Avatare: "Max Berger" → "MB". */
export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
}

/** Initialen aus Vor- und Nachname (Agent-Schnittstelle fuer Avatar). */
export function getInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}
